import React, { useState } from 'react';
import * as IoC from 'typescript-ioc';
import classnames from 'classnames';
import { isSameDay } from 'date-fns';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { getFormattedTimezoneOffset, utcToZonedTime } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import OperatorColumn from '../../../../global/operator-column/operator-column';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams, IRoomConnections } from '../../../../../api/connections.dao';
import Text from '../../../../global/text/text';
import ChevronSVG from '../../../../../images/svg/icons8-expand-arrow';
import DefaultButton from '../../../../global/default-button/default-button';
import { ConfirmDeletePopover, IDeleteConnectionDetails } from '../../shared/connections-list/_connection-delete-confirm-popover';
import { ConnectionListItem } from '../_connection-list-item';
import { getCompleteRoomName } from '../../../../../infrastructure/utils/common.utils';
import { WebinarReportsViewModel } from '../../shared/webinar-report/webinar-reports.viewmodel';
import { MultiSelectOption } from '../../../../global/multi-select-list/_multi-select-option';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../../tracking-service/tracking-values';
import { editText } from '../../../../../constants/common-labels.constants';
import { MeetingApiParams } from '../../shared/connections-list/_connection-form/connection-form.viewmodel';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import KnovioPreviewLinkPopover from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../../shared/knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';
import { IConfigurePassportDisplayFormProps } from '../../shared/configure-passport-display/configure-passport-display.viewmodel';

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

interface IProps extends IRoomConnections {
    eventTimezone: string;
    toggleRoomMeetings: () => void;
    showDates: boolean;
    onEdit: (roomId: number, path?: OpenFrom) => void;
    isSelected: boolean;
    cloneConnection: (connection: IConnectionDetailsDTO) => void;
    editRoomConnection: (roomId: number, connection: IConnectionDetailsDTO) => void;
    showConfigurePassportForm: (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => void;
    deleteRoomConnection: (
        roomId: number,
        data: IDeleteConnectionRequestParams,
        onSuccess?: (updatedDetails?: IConnectionDetailsDTO) => void,
        connectionDetails?: IDeleteConnectionDetails
    ) => void;
    isRoomEditable: boolean;
    webinarReportsVM?: WebinarReportsViewModel;
    isRoomSelected?: boolean;
    bulkOperatorsUpdateFormClosed?: boolean;
    handleSelectRoom?: (roomId: number) => void;
    meetingsQuantity?: number;
    handleTrackingEvent?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS) => void;
    handleTroubleshoot: (connection: IConnectionDetailsDTO) => void;
    trackHostJoinClick: (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom) => void;
    meetingParams: MeetingApiParams;
    shouldDisableDeleteButton: (connectios: IConnectionDetailsDTO) => boolean;
}

const RoomConnectionsListItem = (props: IProps) => {
    const [deletePopoverConnection, setDeletePopoverConnection] = useState<IConnectionDetailsDTO | null>(null);

    const {
        roomId,
        name,
        label,
        eventTimezone,
        startDateTime,
        endDateTime,
        showDates,
        cloneConnection,
        toggleRoomMeetings,
        editRoomConnection,
        showConfigurePassportForm,
        defaultConnectionId,
        isSelected,
        onEdit,
        isRoomEditable,
        deleteRoomConnection,
        webinarReportsVM,
        isRoomSelected,
        handleSelectRoom,
        bulkOperatorsUpdateFormClosed,
        meetingsQuantity,
        handleTrackingEvent,
        handleTroubleshoot,
        trackHostJoinClick,
        meetingParams,
        shouldDisableDeleteButton,
    } = props;
    let startTime;
    let endTime;
    let startTimeInEventTimezone;
    let endTimeInEventTimezone;
    let isEventTimezoneTimeDifferent;
    let timeZoneOffset;
    let localDayString;
    let eventDayString;

    const knovioPreviewLinkPopoverInstance = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Connection);

    if (startDateTime && endDateTime) {
        const startDate = new Date(startDateTime);
        const endDate = new Date(endDateTime);
        startTime = dateTimeFormatter.dateToFormattedString(startDate, dateTimeFormatter.getLocalTimeFormat());
        endTime = dateTimeFormatter.dateToFormattedString(endDate, dateTimeFormatter.getLocalTimeFormat());
        startTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(startDate, dateTimeFormatter.getLocalTimeFormat(), eventTimezone);
        endTimeInEventTimezone = dateTimeFormatter.dateToFormattedString(endDate, dateTimeFormatter.getLocalTimeFormat(), eventTimezone);
        isEventTimezoneTimeDifferent = startTimeInEventTimezone !== startTime;
        timeZoneOffset = getFormattedTimezoneOffset(startDate);
        localDayString = dateTimeFormatter.dateToFormattedString(startDate, DateFnsFormats.StringifiedFullDayMonthNumericDateYear);
        eventDayString = dateTimeFormatter.dateToFormattedString(startDate, DateFnsFormats.StringifiedFullDayMonthNumericDateYear, eventTimezone);
    }

    const handleEditClick = e => {
        e.stopPropagation();
        onEdit(roomId, OpenFrom.Connections);
    };

    const cellEffectClasses = classnames({
        'active': isSelected,
        'hoverable': !isSelected,
    });

    const isMeetingsExists = !!props.meetingIds?.length;
    const onOpenKnovioPreviewLinkPopover = (connection: IConnectionDetailsDTO) => {
        if (connection?.type === ConnectionType.Knovio && hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION)) {
            knovioPreviewLinkPopoverInstance.setConnectionInfo({
                ...meetingParams,
                meetingConnectionDetailsId: connection.meetingConnectionId
            });

            knovioPreviewLinkPopoverInstance.openPopover();
        }
    };

    const handleConfigurePassportForm = (connection: IConnectionDetailsDTO): void => {
        showConfigurePassportForm({
            accountId: meetingParams.accountId,
            eventId: meetingParams.eventId,
            meetingConnectionId: connection.meetingConnectionId,
            roomId: roomId
        }, connection);
    };

    return (
        <tr className='room-list-item' onClick={toggleRoomMeetings} onDoubleClick={isRoomEditable ? handleEditClick : undefined}>
            <td className={cellEffectClasses}>
                <div className='room-name'>
                    {isMeetingsExists &&
                        <div className={classnames(
                            'oe-icon', 'oe-icon__svg', 'expand-arrow',
                            { 'expand-arrow__up': props.isExpanded }
                        )}>
                            <ChevronSVG />
                        </div>}
                    <Text
                        message={getCompleteRoomName({ name: name, label: label })}
                        withEllipsis
                    />
                </div>
            </td>
            {startDateTime && endDateTime ? <td className={`${cellEffectClasses} column-with-filters`}>
                {showDates &&
                    <div className='meeting-list-item__header--date-and-time' data-testid='room-day'>
                        {eventDayString}
                    </div>}
                <div className='meeting-list-item__header--date-and-time'>
                    {startTimeInEventTimezone} - {endTimeInEventTimezone}
                </div>

                {!isSameDay(new Date(startDateTime), utcToZonedTime(startDateTime, eventTimezone)) &&
                    <div className='meeting-list-item__text meeting-list-item__text--small meeting-list-item__text--date-and-time'
                        data-testid='local-day'>
                        {localDayString}
                    </div>}

                {isEventTimezoneTimeDifferent &&
                    <div className='meeting-list-item__text meeting-list-item__text--small'>
                        {startTime} - {endTime} ({timeZoneOffset})
                    </div>}
            </td> : <td className={cellEffectClasses} />}

            <td className={cellEffectClasses}>
                {props.presenters && props.presenters.map((po, k) => {
                    return (
                        <div key={'presenter-' + k} className='w-100'>
                            <div className='meeting-list-item-row'>
                                <Text className='meeting-list-item__header' message={po.name} withEllipsis />
                                <Text className='meeting-list-item__text' message={`, Presenter`} withEllipsis />
                            </div>
                        </div>
                    );
                })}
            </td>

            <td className={cellEffectClasses}>
                {props.connections && props.connections.map(c =>
                    <ConnectionListItem
                        key={`room-connection-${c.meetingConnectionId}`}
                        connection={c}
                        eventTimezone={eventTimezone}
                        cloneConnection={cloneConnection}
                        isDefault={c.meetingConnectionId === defaultConnectionId}
                        handleOnChange={(connection: IConnectionDetailsDTO) => editRoomConnection(roomId, connection)}
                        showConfigurePassportForm={(connection: IConnectionDetailsDTO) => {
                            handleConfigurePassportForm(connection);
                        }}
                        setDeletePopoverConnection={setDeletePopoverConnection}
                        webinarReportsVM={webinarReportsVM}
                        isRoom
                        handleTroubleshoot={handleTroubleshoot}
                        trackHostJoinClick={trackHostJoinClick}
                        handleTrackingEvent={handleTrackingEvent}
                        meetingParams={meetingParams}
                        hideChevron
                        onOpenKnovioPreviewLinkPopover={onOpenKnovioPreviewLinkPopover}
                        shouldDisableDeleteButton={shouldDisableDeleteButton(c)}
                    />)}
            </td>

            <td
                onClick={e => e.stopPropagation()}
                className={cellEffectClasses}
            >
                {!!meetingsQuantity &&
                    <MultiSelectOption
                        onClick={() => handleSelectRoom(roomId)}
                        isSelected={isRoomSelected}
                        isDisabled={bulkOperatorsUpdateFormClosed}
                    />
                }
            </td>

            <td className={cellEffectClasses}>
                {props.operators && <OperatorColumn operators={props.operators} />}
            </td>
            <td className={classnames(cellEffectClasses, 'align-right')}>
                {roomId && isRoomEditable && <DefaultButton
                    onClick={handleEditClick}
                    className='visible-while-hovered'
                    modifier='slim'
                    text={editText()}
                />}
            </td>
            <KnovioPreviewLinkPopover vm={knovioPreviewLinkPopoverInstance}/>

            <ConfirmDeletePopover
                handleOnDelete={(
                    data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails
                ) => deleteRoomConnection(roomId, data, null, connectionDetails)}
                deletePopoverConnection={deletePopoverConnection}
                setDeletePopoverConnection={setDeletePopoverConnection}
            />
        </tr>
    );
};

export { RoomConnectionsListItem };

